import React, { useContext, useEffect, useState } from 'react';

import _ from 'lodash';

import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import ImageClusterPreview from './ImageClusterPreview';
import ImageClusterFinder from './ImageClusterFinder';
import BadgeColorHash from '../common/BadgeColorHash';
import LegoContextSummary from '../lego/LegoContextSummary';
import { IconButton } from '../common/IconButton';

const SOURCE_LABELS = {
  link_clusters: 'linked manually',
  global_template_link: 'global template link',
  confirm_link: 'confirmed link',
  merge_clusters: 'merged',
  fusebox_template: 'same fusebox template',
  reject_twin: 'rejected twin',
  reject_link: 'rejected link',
};

/** Opens both clusters side by side in the image-clusters manager. */
function openPairInManager(clusterIdA, clusterIdB) {
  const filters = encodeURIComponent(JSON.stringify({ _id: { $in: [clusterIdA, clusterIdB] } }));
  window.open(`/legos/image-clusters?filters=${filters}`, '_blank');
}

/**
 * Lists the clusters related to `clusterId` in the image_cluster_links collection: direct
 * positive links (human + fusebox-template), rejected pairs, and the rest of the template group
 * (transitive closure of SAME through intermediate clusters), each with its relation details and
 * an ImageClusterPreview to eyeball the match. Relations can be edited: add a link ("same
 * template") or a rejection ("confirmed different") by picking the other cluster with
 * ImageClusterFinder (any model, ranked by image similarity), or delete an existing record.
 */
export default function ModalLinkedClusters({ clusterId }) {
  const { page } = useContext(LegoAdminPageContext);
  const [links, setLinks] = useState(null);
  const [transitive, setTransitive] = useState([]);
  const [context, setContext] = useState(null);
  const [clusterType, setClusterType] = useState('fusebox-broad');
  const [imageIds, setImageIds] = useState([]);
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(null); // 'link' | 'reject' | null
  const [refreshKey, setRefreshKey] = useState(0);

  const linksService = page.service('services/data/image-cluster-links');

  useEffect(() => {
    linksService.find({ query: { clusterId } })
      .then((res) => {
        setLinks(res.links || []);
        setTransitive(res.transitive || []);
        setContext(res.context || null);
        setClusterType(res.clusterType || clusterType);
      })
      .catch((err) => setError(String(err?.message || err)));
  }, [clusterId, refreshKey]);

  // The cluster's image ids, so the finder can rank candidate clusters by image similarity.
  useEffect(() => {
    page.service('services/data/image-clusters').find({ query: { _id: { $in: [clusterId] } } })
      .then((res) => setImageIds(_.map(res?.[0]?.images || [], (img) => String(img.id))))
      .catch(() => setImageIds([]));
  }, [clusterId]);

  const addLink = (otherCluster) => {
    const otherId = String(otherCluster?._id || '');
    if (!otherId)
       return;
    if(otherId === clusterId) {
      alert('Cannot link a cluster to itself');
      return;
    }
    page.runAsync(async () => {
      await linksService.create({ clusterIdA: clusterId, clusterIdB: otherId, polarity: picking });
      setPicking(null);
      setRefreshKey((k) => k + 1);
    }, picking === 'reject' ? 'Marking clusters as different...' : 'Linking clusters...');
  };

  const removeLink = (link) => {
    const what = link.polarity === 'link' ? 'SAME-template link' : 'DIFFERENT rejection';
    if (!confirm(`Delete the ${what} to ${link.clusterId}?\nSources: ${link.sources.join(', ') || '—'}`)) return;
    page.runAsync(async () => {
      await linksService.remove(`${clusterId}_${link.clusterId}_${link.polarity}`);
      setRefreshKey((k) => k + 1);
    }, 'Removing link...');
  };

  if (error) return <div className={'alert alert-danger m-3'}>{error}</div>;
  if (!links) return <div className={'text-secondary p-4'}>Loading linked clusters…</div>;

  if (picking) {
    return <div className={'d-flex flex-column'} style={{ height: '100%', overflow: 'hidden' }}>
      <div className={`p-2 d-flex align-items-center ${picking === 'link' ? 'bg-light-success' : 'bg-light-danger'}`}
        style={{ gap: '8px' }}>
        <button className={'btn btn-secondary btn-sm'} onClick={() => setPicking(null)}>Cancel</button>
        <span>
          Pick the cluster that is <strong>{picking === 'link' ? 'the SAME template' : 'confirmed DIFFERENT'}</strong>
          {' '}as <BadgeColorHash id={clusterId} /> (any model, sorted by image similarity):
        </span>
      </div>
      <div className={'flex-grow-1 overflow-auto'}>
        <ImageClusterFinder clusterType={clusterType}
                            vehicleContext={{ modelId: context?.modelId }}
                            findSimilarToImages={imageIds.slice(0, 10)}
                            onClusterSelected={addLink} />
      </div>
    </div>;
  }

  const positives = links.filter((l) => l.polarity === 'link');
  const rejected = links.filter((l) => l.polarity === 'reject');
  const polarityClass = { link: 'bg-light-success', reject: 'bg-light-danger' };

  const renderLink = (link) => <div key={link.clusterId + link.polarity}
    className={`d-flex align-items-center border-bottom p-2 ${polarityClass[link.polarity]}`} style={{ gap: '12px' }}>
    <div style={{ flex: '0 0 220px' }}>
      <span className={`badge ${link.polarity === 'link' ? 'badge-success' : 'badge-danger'} mr-1`}>
        {link.polarity === 'link' ? 'SAME' : 'DIFFERENT'}
      </span>
      <BadgeColorHash id={link.clusterId} className={'zoom-90'} />

      <div className={'mt-1'}>
        <LegoContextSummary context={link.context || {}} />
      </div>

      <div className={'small text-muted mt-1'}>
        {link.sources.map((s) => SOURCE_LABELS[s] || s).join(', ')}
        {link.mergeKeepClusterId === link.clusterId ? ' (kept side)' : ''}
      </div>

      {link.templateIds.length ? <div className={'small text-muted monospace'}>
        {link.templateIds.join(', ')}
      </div> : null}
      {link.updatedBy ? <div className={'small text-muted'}>
        by {link.updatedBy}{link.updatedAt ? ` · ${String(link.updatedAt).slice(0, 10)}` : ''}
      </div> : null}
      <a href={'#'} className={'small'} onClick={(e) => { e.preventDefault(); openPairInManager(clusterId, link.clusterId); }}>
        ⧉ open both in manager
      </a>
      <div>
        <IconButton icon={'delete'} level={'danger'} className={'zoom-90'}
          title={'Delete this record from image_cluster_links (template-deduced links may be re-created by the sync script)'}
          onClick={() => removeLink(link)}>
          remove
        </IconButton>
      </div>
    </div>
    <ImageClusterPreview clusterId={link.clusterId} size={'100'} maxImages={6} />
  </div>;

  const renderTransitiveMember = (member) => <div key={member.clusterId}
    className={'d-flex align-items-center border-bottom p-2 bg-light'} style={{ gap: '12px' }}>
    <div style={{ flex: '0 0 220px' }}>
      <span className={'badge badge-success mr-1'} style={{ opacity: 0.65 }}
        title={'In the same template group through intermediate links (transitive closure of SAME)'}>
        SAME · {member.hops} hops
      </span>
      <BadgeColorHash id={member.clusterId} className={'zoom-90'} />

      <div className={'mt-1'}>
        <LegoContextSummary context={member.context || {}} />
      </div>

      <div className={'small text-muted mt-1'}>
        linked through intermediate clusters — to sever, remove the direct links along the chain
      </div>

      <a href={'#'} className={'small'} onClick={(e) => { e.preventDefault(); openPairInManager(clusterId, member.clusterId); }}>
        ⧉ open both in manager
      </a>
    </div>
    <ImageClusterPreview clusterId={member.clusterId} size={'100'} maxImages={6} />
  </div>;

  return <div className={'p-3'}>
    <div className={'mb-3 d-flex align-items-center'} style={{ gap: '12px' }}>
      <div style={{ flex: '0 0 220px' }}>
        <div className={'small text-muted mb-1'}>This cluster:</div>
        <BadgeColorHash id={clusterId} className={'zoom-90'} />
        <div className={'mt-1'}>
          <LegoContextSummary context={context || {}} />
        </div>
      </div>
      <ImageClusterPreview clusterId={clusterId} size={'100'} maxImages={6} />
    </div>

    <div className={'mb-3 d-flex'} style={{ gap: '8px' }}>
      <IconButton icon={'add_link'} fill level={'success'} onClick={() => setPicking('link')}>
        Link a cluster (same template)
      </IconButton>
      <IconButton icon={'link_off'} fill level={'danger'} onClick={() => setPicking('reject')}>
        Mark a cluster as different
      </IconButton>
    </div>

    {!links.length && !transitive.length ? <div className={'text-secondary'}>
      No linked or rejected clusters in <span className={'monospace'}>image_cluster_links</span>.
    </div> : null}

    {positives.length ? <>
      <h6>Same template ({positives.length})</h6>
      {positives.map(renderLink)}
    </> : null}

    {transitive.length ? <>
      <h6 className={'mt-3'}>Same template — indirect ({transitive.length})</h6>
      {transitive.map(renderTransitiveMember)}
    </> : null}

    {rejected.length ? <>
      <h6 className={'mt-3'}>Confirmed different ({rejected.length})</h6>
      {rejected.map(renderLink)}
    </> : null}
  </div>;
}
